// eslint-disable-next-line no-unused-vars
import React, { useMemo, useCallback, useState } from 'react';
import { object, bool, func, array, number } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';

import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import ProductOrderItem from 'Containers/Products/ProductOrderItem';
import OrderProductsComponent from 'Components/Order/OrderProducts';
import OrderProductsModal from 'Containers/Order/OrderProductsModal';
import { CURRENCY_RON } from 'Constants';
import useAnalytics from 'Hooks/useAnalytics';

const OrderProducts = ({
  order,
  products,
  editable = true,
  shouldPromptChanges = true,
  onAddProduct,
  onUpdateProduct = noop,
  onSaveProductUpdate,
  onRemoveProduct,
  onCancelRemoveProduct,
  onCancelAllChanges,
  selectedDSCountry,
  hasProductsWithChanges,
  loadingOrderUpdate,
  loadingCancelChanges,
  expandRowProgrammatically,
  unreservableProducts,
  unavailableProductIds,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [actionsDisabled, setActionsDisabled] = useState(false);
  const { data: user } = useUser();
  const { isExternalReseller: IS_EXTERNAL_RESELLER } = usePermissions();
  const analytics = useAnalytics();

  const stockReservation = order?.statusId === 2;
  let defaultCurrency;

  if (IS_EXTERNAL_RESELLER) {
    defaultCurrency = order?.id ? order?.currencyId : user?.defaultCurrency;
  } else {
    defaultCurrency = order?.id ? order?.currencyId : CURRENCY_RON;
  }

  const tableHeaders = useMemo(() => {
    const headers = [{
      key: 'name',
      header: t('product.product').toUpperCase(),
    }, {
      key: 'quantity',
      header: t('product.quantity').toUpperCase(),
    }, {
      key: 'unitPriceNoVat',
      header: IS_EXTERNAL_RESELLER ? t('product.unitPrice') : t('product.unitPriceNoVat'),
    }, {
      key: 'totalPriceNoVat',
      header: IS_EXTERNAL_RESELLER ? t('product.totalPrice') : t('product.totalPriceNoVat'),
    }];
    if (order?.dropShipment) {
      headers.push({
        key: 'totalPriceWithVat',
        header: t('product.priceWithVatForFinalClient'),
      });
    }
    if (editable) {
      headers.push({
        key: 'delete',
        header: '',
      });
    }
    return headers;
  }, [order?.dropShipment, editable, IS_EXTERNAL_RESELLER]);

  const handleOnSaveProduct = async product => {
    setActionsDisabled(true);
    try {
      await onSaveProductUpdate(product);
    } finally {
      setActionsDisabled(false);
    }
  };

  const handleOnPriceChange = product => {
    const payload = {
      productId: product.productId,
      quantity: product.quantity,
      minQuantity: product.minQuantity,
      ...(!product.hasPrice && {
        supplierId: product.supplierId,
        supplierOfferId: product.supplierOfferId,
      })
    };

    if (product.promotion?.promotionId) {
      payload.appliedPromotions = [product.promotion.promotionId];
      if (product.promotion?.type === 'DSC') {
        payload.promoItems = {};
      } else {
        payload.promoItems = {
          [product.promotion.promotionId]: product.promoItems[product.promotion.promotionId].map(prod => {
            const p = {
              productId: prod.productId,
              quantity: prod.quantity,
            };
            if (prod.addedCOD) {
              p.cashOnDeliveryValue = prod.cashOnDeliveryValue;
            }
            return p;
          }),
        };
      }
    }

    if (!product.promoUpdated) {
      payload.cashOnDeliveryValue = product.cashOnDeliveryValue;
    }
    order.updateItem(payload);
  };

  const handleOnOpenModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleOnCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleOnAddProduct = product => {
    analytics.track('reservation_modal_add_product', { product_id: product.id });
    if (onAddProduct(product)) {
      setModalOpen(false);
    }
  };

  const splitProductsBySupplier = useCallback(supplierProducts => {
    const groups = new Map();

    if (!supplierProducts) {
      return [];
    }

    supplierProducts.forEach(product => {
      const key = product.supplierId ?? 'default';

      if (!groups.has(key)) {
        groups.set(key, []);
      }

      groups.get(key).push(product);
    });

    const result = Array.from(groups.entries()).map(([supplierId, items]) => {
      const totalValue = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);

      return {
        supplierId,
        products: items,
        totalValue,
        ...(supplierId !== 'default' && items.length > 0 && {
          supplierOfferDeliveryTimeDays: items[0].supplierOfferDeliveryTimeDays
        })
      };
    });

    // show NOD products first
    // eslint-disable-next-line no-nested-ternary
    return result.sort((a, b) => (a.supplierId === 'default' ? -1 : b.supplierId === 'default' ? 1 : 0));
  }, []);

  const renderProducts = useCallback(
    productsData => (productsData || []).map(product => {
      const id = (product.productId || product.itemId || product.orderDetailId || product.id);
      // let key = id;
      // if (product.promotion) {
      //   key = `promotion:${product.promotion.promotionId}_${id}`;
      // }
      // if (product.appliedPromotions) {
      //   key = `promotion:${product.appliedPromotions.at(0)?.promotionId}_${id}`;
      // }
      // const unitPrice = 'unitPrice' in product
      //   ? product.unitPrice
      //   : product.price;
      // let totalPrice = unitPrice * product.quantity;
      // if ('ronTotalPrice' in product) {
      //   totalPrice = product.ronTotalPrice;
      // } else if ('totalPriceRon' in product) {
      //   totalPrice = product.totalPriceRon;
      // }
      // let totalPromoPrice = 'ronPromoPrice' in product
      //   ? product.ronPromoPrice * product.quantity
      //   : unitPrice * product.quantity;
      // if ('totalPromoPriceRon' in product) {
      //   totalPromoPrice = product.totalPromoPriceRon;
      // }
      const { totalPrice, totalPromoPrice, price: unitPrice } = product;
      let canDelete = editable;
      if (
        stockReservation
        && products.filter(prod => !prod.new).length === 1
      ) {
        canDelete = false;
      }
      const { promoItems, appliedPromotions, promotion } = product;
      const promotionId = promotion?.promotionId || appliedPromotions?.at(0)?.promotionId;
      const orderItem = order.items.find(item => {
        if (promotionId) {
          return (
            item.id === id
            && item.appliedPromotions?.at(0) === promotionId
          );
        }
        return (
          item.id === id
          && !item.appliedPromotions
        );
      });
      if (
        orderItem
        && 'cashOnDeliveryValue' in orderItem
      ) {
        // eslint-disable-next-line no-param-reassign
        product.cashOnDeliveryValue = orderItem.cashOnDeliveryValue;
      } else if (!order.id) {
        // eslint-disable-next-line no-param-reassign
        delete product.cashOnDeliveryValue;
      }
      if (
        promotionId
        && Object.entries(promoItems || {}).length
      ) {
        const mergedPromoItems = Object.entries(promoItems).reduce((acc, [promoKey, items]) => {
          acc[promoKey] = items.map(promoItem => {
            const payload = {
              ...promoItem,
            };
            const orderPromoItems = (orderItem?.promoItems || {})[promoKey] || [];
            const promoItemId = promoItem.productId || promoItem.orderDetailId || promoItem.id;
            const orderPromoItem = orderPromoItems.find(item => item.id === promoItemId);
            if (
              orderPromoItem
              && 'cashOnDeliveryValue' in orderPromoItem
            ) {
              payload.cashOnDeliveryValue = orderPromoItem.cashOnDeliveryValue;
            } else if (!order.id) {
              delete payload.cashOnDeliveryValue;
            }
            // const promoUnitPrice = 'unitPrice' in promoItem
            //   ? promoItem.unitPrice
            //   : promoItem.price;
            // let totalPromoItemPrice = promoUnitPrice * promoItem.quantity;
            // if ('ronTotalPrice' in promoItem) {
            //   totalPromoItemPrice = promoItem.ronTotalPrice;
            // } else if ('totalPriceRon' in promoItem) {
            //   totalPromoItemPrice = promoItem.totalPriceRon;
            // }
            // let totalPromoItemPromoPrice = 'ronPromoPrice' in promoItem
            //   ? promoItem.ronPromoPrice * promoItem.quantity
            //   : unitPrice * promoItem.quantity;
            // if ('totalPromoPriceRon' in promoItem) {
            //   totalPromoItemPromoPrice = promoItem.totalPromoPriceRon;
            // }

            const { price: promoUnitPrice, totalPrice: totalPromoItemPrice, totalPromoPrice: totalPromoItemPromoPrice } = promoItem;
            return {
              ...payload,
              productId: promoItem.productId || promoItem.id,
              pictureUrl: payload.pictureUrl || payload?.pictures?.at(0)?.urlThumbnailPicture,
              price: promoUnitPrice,
              ronPrice: 'ronPrice' in promoItem
                ? promoItem.ronPrice
                : promoUnitPrice,
              promoPrice: 'promoPrice' in promoItem
                ? promoItem.promoPrice
                : promoUnitPrice,
              ronPromoPrice: 'ronPromoPrice' in promoItem
                ? promoItem.ronPromoPrice
                : promoUnitPrice,
              totalPrice: totalPromoItemPrice,
              totalPromoPrice: totalPromoItemPromoPrice,
            };
          });
          return acc;
        }, {});
        Object.assign(promoItems, mergedPromoItems);
      }

      return (
        <ProductOrderItem
          key={ product.itemId || product.orderDetailId || product.productId || product.id }
          product={ {
            ...product,
            id,
            productId: product.productId || product.id,
            promoItems,
            // quantity: (orderItem && !product.updated)
            //   ? orderItem.quantity
            //   : product.quantity,
            // cashOnDeliveryValue: orderItem
            //   ? orderItem.cashOnDeliveryValue
            //   : product.cashOnDeliveryValue || 0,
            // userInputCashOnDeliveryValue: order.id
            //   ? product.cashOnDeliveryValue || 0
            //   : orderItem?.userInputCashOnDeliveryValue,
            // TODO: replace with actual currency when API will support multi-currency values in cart
            // currency: user.type === 2 ? user?.defaultCurrency : 'ron',
            currency: 'ron', // order?.currency || product.currency,
            price: unitPrice,
            promoPrice: 'promoPrice' in product
              ? product.promoPrice
              : unitPrice,
            totalPrice,
            totalPromoPrice,
          } }
          ds={ order?.dropShipment }
          editable={ editable }
          orderId={ order.id }
          shouldPromptChanges={ shouldPromptChanges }
          canDelete={ canDelete || product.new }
          onSave={ handleOnSaveProduct }
          onRemove={ onRemoveProduct }
          onCancelRemove={ onCancelRemoveProduct }
          onUpdate={ onUpdateProduct }
          onChangePrice={ handleOnPriceChange }
          actionsDisabled={ actionsDisabled }
          selectedDSCountry={ selectedDSCountry }
          dsCurrency={ order?.dropShipmentCurrency?.currencyId }
          defaultCurrency={ defaultCurrency }
          expandRowProgrammatically={ expandRowProgrammatically }
          unreservableProducts={ unreservableProducts }
          unavailableProductIds={ unavailableProductIds }
        />
      );
    }),
    [
      products,
      actionsDisabled,
      order.dropShipment,
      order.id,
      order.items,
      selectedDSCountry,
      user?.defaultCurrency,
      unreservableProducts,
      unavailableProductIds,
    ],
  );

  const productsBySuppliers = splitProductsBySupplier(products);

  return (
    <>
      <OrderProductsModal
        products={ products }
        onAddProduct={ handleOnAddProduct }
        onClose={ handleOnCloseModal }
        open={ modalOpen }
      />
      { (productsBySuppliers.length > 0 ? productsBySuppliers : [{}]).map((items, index) => (
        <OrderProductsComponent
          key={ items.supplierId }
          id={ order?.id }
          loading={ !products }
          editable={ editable }
          columns={ tableHeaders }
          onOpenModal={ handleOnOpenModal }
          ds={ order?.dropShipment }
          actionsDisabled={ actionsDisabled }
          defaultCurrency={ defaultCurrency }
          onSaveProductUpdate={ onSaveProductUpdate }
          onCancelAllChanges={ onCancelAllChanges }
          hasProductsWithChanges={ hasProductsWithChanges }
          loadingOrderUpdate={ loadingOrderUpdate }
          loadingCancelChanges={ loadingCancelChanges }
          supplierId={ items.supplierId }
          supplierIndex={ index }
          supplierTotalValue={ items.totalValue }
          supplierOfferDeliveryTimeDays={ items.supplierOfferDeliveryTimeDays }
        >
          { renderProducts(items.products) || <span></span> }
        </OrderProductsComponent>
      )) }
    </>
  );
};
OrderProducts.displayName = 'OrderProductsContainer';
OrderProducts.propTypes = {
  order: object,
  editable: bool,
  shouldPromptChanges: bool,
  products: array,
  onSaveProductUpdate: func,
  onRemoveProduct: func,
  onCancelRemoveProduct: func,
  onCancelAllChanges: func,
  onUpdateProduct: func,
  onAddProduct: func,
  selectedDSCountry: number,
  hasProductsWithChanges: bool,
  loadingOrderUpdate: bool,
  loadingCancelChanges: bool,
  expandRowProgrammatically: func,
  unreservableProducts: array,
  unavailableProductIds: array,
};

export default OrderProducts;
